// @ts-ignore
import type { Song, Images, ImageSize } from '~/types'

/**
 * Get the preferred size of an image from the images object, or the next largest available - or smaller if `preferSmallerFallback` is true.\m
 * Will return an placeholder image if no images is defined.
 *
 * @returns {string} - Image URL from the image object, or a placeholder if not defined
 * @param {string} images - The images object from the API
 * @param {string} size - The size of the image to get
 * @param {string} fallbackType - The type of image to get if the image is not defined
 * @param {boolean} preferSmallerFallback - Whether to prefer a smaller image rather than larger is exact image is not available
 */
export const useGetImageSize = () => (
	images: Images,
	size: ImageSize,
	fallbackType: 'song' | 'playlist' | 'artist' | 'background',
	preferSmallerFallback: boolean
): string => {
	if (!images) {
		return `/img/${fallbackType}-placeholder.svg`
	}

	const sizes = {
		tiny: 1,
		small: 2,
		medium: 3,
		large: 4,
		xlarge: 5
	}

	const placeholders = [
		'song',
		'artist',
		'background',
		'playlist'
	]

	const imageArray = Object.entries(sizes).map(([key, sizeValue]) => {
		const isDefined = images[key as keyof typeof sizes]
		return {
			data: isDefined,
			size: key as keyof typeof sizes,
			sizeValue
		}
	}).sort((a, b) => a.sizeValue - b.sizeValue)

	const exactSize = imageArray.find(n => n.size === size && n.data && n.data.url)
	const sizeIndex = sizes[size as keyof typeof sizes] ? sizes[size as keyof typeof sizes] : 3

	const getLarger = (): string | undefined => {
		for (let i = sizeIndex; i <= 5; i++) {
			const img = imageArray[i - 1]
			if (img.data && img.data.url) {
				return img.data.url
			}
		}
	}
	const getSmaller = (): string | undefined => {
		for (let i = sizeIndex; i > 0; i--) {
			const img = imageArray[i - 1]
			if (img.data && img.data.url) {
				return img.data.url
			}
		}
	}
	const preferSmaller = (): string | undefined => {
		return getSmaller() || getLarger()
	}
	const preferLarger = (): string | undefined => {
		return getLarger() || getSmaller()
	}

	if (!sizes[size as keyof typeof sizes]) {
		console.error(`Invalid image size: "${size}", valid image sizes:
		'tiny'
		'small'
		'medium'
		'large'
		'xlarge'`)
		return preferLarger() || 'fallback-url'
	}
	if (!placeholders.includes(fallbackType)) {
		console.error(`Invalid placeholder: "${fallbackType}", valid placeholder fallbacks:
		'song'
		'artist'
		'background'
		'playlist'`)
		return preferLarger() || 'fallback-url'
	} else if (exactSize && exactSize.data && exactSize.data.url) {
		return exactSize.data.url
	} else {
		const finalUrl = preferSmallerFallback ? preferSmaller() : preferLarger()
		return finalUrl || `/img/${fallbackType}-placeholder.png`
	}
}

export const useGetSingaColor = () => (id: number): string => {
	const colors = [
		'#DC6556',
		'#12B961',
		'#BB9A11',
		'#97857B',
		'#FFFFFF',
		'#5A50AF',
		'#4D4D4D',
		'#A93223',
		'#74F1AF',
		'#EECD44',
		'#F5EBDC',
		'#B2B2B2',
		'#AFA4D7',
		'#757575'
	]
	return colors[id % colors.length]
}

export const useMakeId = async () => {
	const getRandomValues = (length: number) => {
		const array = new Uint8Array(length)
		crypto.getRandomValues(array)
		return array
	}

	const uuid = ([1e7] as any + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c: string) =>
		(Number(c) ^ getRandomValues(1)[0] & 15 >> (Number(c) / 4)).toString(16)
	)

	// Convert the UUID to ArrayBuffer
	const encoder = new TextEncoder()
	const uuidArrayBuffer = encoder.encode(uuid)

	// Calculate SHA-1 hash using Web Crypto API
	const hashBuffer = await crypto.subtle.digest('SHA-1', uuidArrayBuffer)

	// Convert the hash to a hex string
	const hashArray = Array.from(new Uint8Array(hashBuffer))
	const hash = hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('')

	return hash
}

export const useTruncate = (title: string, limit = 50) => {
	if (title.length > limit) {
		return title.slice(0, limit) + '…'
	}
	return title
}

export const useGetOS = () => {
	const { isIos, isWindows, isMacOS, isAndroid } = useDevice()
	let os = 'Unknown'

	if (isIos) {
		os = 'iOS'
	}
	if (isWindows) {
		os = 'Windows'
	}
	if (isMacOS) {
		os = 'MacOS'
	}
	if (isAndroid) {
		os = 'Android'
	}
	return os
}

export const createMetadescription = (songs: Array<Song>, length = 160) => {
	const metaDescriptionSongList: string[] = []
	for (let i = 0; i < songs.length; i++) {
		const { getAllArtists } = useArtistNames(songs[i].artists)
		const artists = getAllArtists()
		metaDescriptionSongList.push(
			`${i + 1}. ${artists} – ${songs[i].name}`
		)
	}
	const metaDescription = useTruncate(metaDescriptionSongList.join(', '), length)
	return metaDescription
}

export const consoleLogSingaLogo = () => {
	const singaText = `
	%c░░░░░░░░░░░%c▒▓██████▓%c░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
	░░░%c▒█▓▒%c░░%c▓██▓%c░░░░░░%c█▓%c░░░%c▒██▒%c░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
	░░░░%c█▒%c░░%c▓██▒%c░░░░░%c░▒▓%c░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
	░░░░░%c▒▒▒███▓▓▒▒▒▒▒%c░░░░░%c██▓%c░░%c▒██%c░%c▒███▒%c░░░░%c▓███▓██%c░░░░%c▒▓██▓▓█▓%c
	░░░░░░░░░%c▓████▓%c░░░░░░░%c███%c░░%c▒██▓▓████▒%c░░%c▓██▒%c░%c▒██▓%c░░%c▒██▓%c░░%c███%c▒
	░░░░░░░░░░░%c▒▓████%c░░░%c░███%c░░%c▒██▓▓▒▒██▒%c░░%c██▓%c░░░%c███%c░░%c▓██▒%c░░%c▒██%c▒░
	░%c▒███▓▒%c░░░░░░░%c▓███▒%c░%c▓██%c░░░%c███▒%c░%c▒██▒%c░░%c██▓%c░░░%c▓██%c░░%c▓██%c░░░%c▒██▒%c░░
	%c▓█▓%c░░░░░░░░░░░░%c███▓▒██▓%c░░%c▓██▒%c░░%c██▒%c░░%c███%c░░░%c▓██%c░░%c▒██▒%c░░%c▒██▓%c░░░%c
	██%c░░░░░░░░░░░░%c▒███%c░%c███%c░░%c▓██▒%c░░%c██▓%c░%c░▓██▒%c░%c▒▓██▒%c░░%c███%c░░%c▓▓██%c░░▒░
	░%c█▓%c░░░░░░░░░░%c▓██▓%c░░%c███▓▒▒██%c░░░%c███▓▓▒████▒███▒▓▒▓███▓%c░%c▓██▓▒%c░░
	░░%c▒▓▓▒▒▒▒▓▓▓▓▓▒%c░░░░░░░░░░░░░░░░░░░░░░░░%c▒███▒%c░░░░░░░░░░░░░░░░
	░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░%c▓▓███%c░░░░░░░░░░░░░░░░░░
	░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░%c█▓███%c░░░░░░░░░░░░░░░░░░░
	`
	return console.log(singaText, 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent', 'color: #17E87A', 'color: transparent')
}
